import { ErreurApi, type Client } from '../../api/client.js';
import { parentsDe } from './modele.js';
import type { CorpsPersonne, DonneesArbre } from './types.js';

/**
 * Lien de la personne ajoutée avec une personne de l'arbre. La fratrie (ADR 0064) se déduit des parents communs : « frère ou
 * sœur de » reprend tous les parents, « demi-frère ou demi-sœur de » le seul parent commun choisi ; le serveur crée au besoin
 * le parent « à rechercher ».
 */
export type LienAjout =
  | { type: 'enfant_de' | 'parent_de' | 'conjoint_de'; personneId: string }
  | { type: 'frere_soeur_de' | 'demi_frere_soeur_de'; personneId: string; parentCommunId?: string };

export type TypeLien = LienAjout['type'];

export const estFratrie = (lien: LienAjout | undefined): lien is Extract<LienAjout, { type: 'frere_soeur_de' | 'demi_frere_soeur_de' }> =>
  lien?.type === 'frere_soeur_de' || lien?.type === 'demi_frere_soeur_de';

/** Parents communs possibles d'un demi-frère ou d'une demi-sœur : les parents saisis de la personne. */
export const parentsCommunsPossibles = (d: DonneesArbre, personneId: string): string[] => parentsDe(d, personneId);

export interface SaisieAjout {
  prenoms: string;
  nom: string;
  etat: 'vivant' | 'decede' | 'inconnu';
  /** Année (« 2019 ») ou date complète (« 2019-03-08 ») du décès. */
  anneeDeces: string;
  hypothese?: boolean;
  lien?: LienAjout;
}

export interface ResultatValidation {
  valide: boolean;
  erreurs: Record<string, string>;
  corps?: CorpsPersonne;
  /** Vrai quand seule l'année du décès est connue : la date complète reste à trouver (RFC-L54-02). */
  dateCompleteATrouver?: boolean;
}

const MESSAGES = {
  nom: 'Indiquez le nom de famille.',
  anneeManquante: 'Indiquez au minimum l’année du décès : l’ordre des décès conditionne le calcul des parts.',
  anneeFormat: 'L’année du décès comporte quatre chiffres.',
  anneeFuture: 'Cette année du décès est postérieure à aujourd’hui.',
  lienPerdu: 'La personne a été ajoutée, mais son lien de parenté n’a pas pu être enregistré. Ajoutez-le depuis sa fiche.',
  anneePerdue: (annee: string): string =>
    `La personne est enregistrée, mais l’année du décès (${annee}) n’est pas conservée : le dossier n’accepte pas encore une année seule. La date complète du décès reste à compléter depuis sa fiche. Cabinet MAAT`,
  parentCommun: 'Choisissez le parent commun du demi-frère ou de la demi-sœur.',
  inconnue: 'L’ajout n’a pas abouti. Réessayez dans un instant ; si le problème persiste, contactez le Cabinet MAAT.',
} as const;

export function validerAjout(saisie: SaisieAjout, anneeCourante: number = new Date().getFullYear()): ResultatValidation {
  const erreurs: Record<string, string> = {};
  const nom = saisie.nom.trim();
  if (nom === '') erreurs['nom'] = MESSAGES.nom;

  const deces = saisie.anneeDeces.trim();
  const complete = /^\d{4}-\d{2}-\d{2}$/.test(deces);
  if (saisie.etat === 'decede') {
    if (deces === '') erreurs['anneeDeces'] = MESSAGES.anneeManquante;
    else if (!complete && !/^\d{4}$/.test(deces)) erreurs['anneeDeces'] = MESSAGES.anneeFormat;
    else if (Number(deces.slice(0, 4)) > anneeCourante) erreurs['anneeDeces'] = MESSAGES.anneeFuture;
  }
  if (Object.keys(erreurs).length > 0) return { valide: false, erreurs };

  const corps: CorpsPersonne = {
    firstNames: saisie.prenoms.trim(),
    lastName: nom,
    vitalStatus: saisie.etat,
    isHypothesis: saisie.hypothese ?? false,
    role: saisie.lien?.type === 'conjoint_de' ? 'conjoint' : 'heritier',
    ...(saisie.etat === 'decede' ? { deathDate: complete ? deces : null } : {}),
  };
  return { valide: true, erreurs, corps, dateCompleteATrouver: saisie.etat === 'decede' && !complete };
}

export type ResultatAjout =
  | { ok: true; personneId: string; avertissement?: string }
  | { ok: false; message?: string; erreurs?: Record<string, string>; personneId?: string; mandatRequis?: boolean };

const messageDe = (e: unknown): string => (e instanceof ErreurApi ? e.messageUtilisateur : MESSAGES.inconnue);

/**
 * Crée la personne puis son lien, par les vraies routes du contrat. Le serveur écrit le journal d'audit et publie
 * l'événement (`personne.ajout`) ; sans mandat signé au dossier (ni forçage de la dirigeante) il refuse (MANDATE_REQUIRED) et rien n'est
 * créé : le résultat le dit (`mandatRequis`) pour que l'écran ouvre l'avertissement de forçage.
 */
export async function ajouterPersonne(
  saisie: SaisieAjout,
  client: Pick<Client, 'appeler'>,
  contexte: { dossierId: string; donnees?: DonneesArbre },
): Promise<ResultatAjout> {
  const v = validerAjout(saisie);
  if (!v.valide || !v.corps) return { ok: false, erreurs: v.erreurs };
  const lien = saisie.lien;
  const parentCommun = estFratrie(lien) ? (lien.parentCommunId ?? null) : null;
  if (
    lien?.type === 'demi_frere_soeur_de' &&
    parentCommun === null &&
    contexte.donnees !== undefined &&
    parentsCommunsPossibles(contexte.donnees, lien.personneId).length > 1
  ) {
    return { ok: false, erreurs: { parentCommun: MESSAGES.parentCommun } };
  }
  // RFC-L54-001 §2 : tant que le contrat refuse une année seule, on le dit après l'enregistrement, jamais en silence.
  const avertissement = v.dateCompleteATrouver ? { avertissement: MESSAGES.anneePerdue(saisie.anneeDeces.trim()) } : {};
  const parametres = { dossierId: contexte.dossierId };

  if (estFratrie(lien)) {
    // Un seul appel : le serveur reprend les parents et crée au besoin le parent « à rechercher », dans une même transaction.
    try {
      const r = await client.appeler<{ person: { id: string } }>('genealogie.fratrie.ajouter', {
        parametres,
        corps: {
          person: v.corps,
          siblingOfPersonId: lien.personneId,
          siblingKind: lien.type === 'frere_soeur_de' ? 'germain' : 'demi',
          commonParentId: lien.type === 'demi_frere_soeur_de' ? parentCommun : null,
        },
      });
      return { ok: true, personneId: r.person.id, ...avertissement };
    } catch (e) {
      return {
        ok: false,
        message: messageDe(e),
        ...(e instanceof ErreurApi && e.code === 'MANDATE_REQUIRED' ? { mandatRequis: true } : {}),
      };
    }
  }

  let personneId: string;
  try {
    const creee = await client.appeler<{ id: string }>('genealogie.personnes.creer', { parametres, corps: v.corps });
    personneId = creee.id;
  } catch (e) {
    // Couloir G, point 5 : le blocage du mandat n'est plus une impasse ; l'écran propose le forçage tracé (dirigeante) ou la demande.
    return { ok: false, message: messageDe(e), ...(e instanceof ErreurApi && e.code === 'MANDATE_REQUIRED' ? { mandatRequis: true } : {}) };
  }

  if (lien) {
    const relation =
      lien.type === 'conjoint_de'
        ? { kind: 'union', personAId: lien.personneId, personBId: personneId, unionType: 'mariage', filiationType: null }
        : {
            kind: 'filiation',
            personAId: lien.type === 'enfant_de' ? lien.personneId : personneId,
            personBId: lien.type === 'enfant_de' ? personneId : lien.personneId,
            unionType: null,
            filiationType: 'biologique',
          };
    try {
      await client.appeler('genealogie.relations.creer', { parametres, corps: { ...relation, startDate: null, endDate: null } });
    } catch {
      return { ok: false, message: MESSAGES.lienPerdu, personneId };
    }
  }
  return { ok: true, personneId, ...avertissement };
}
